import { useState, useEffect } from 'react';
import { useEventManageContext } from './EventManagePage';
import {
  Layers,
  Music,
  ExternalLink,
  Check,
  AlertTriangle,
  Clock,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { getRequestsByEventId, updateRequest, SongRequest } from '../../services/requests';
import { openUrlFor, playModeOf } from '../../services/playModes';
import { compareManualPositions } from '../../utils/manualQueuePlacement';
import toast from 'react-hot-toast';

// Helper to format duration from ms to mm:ss
function formatDuration(ms?: number): string {
  if (!ms) return '--:--';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function PrepAheadPage() {
  const { event } = useEventManageContext();
  const isLocked = event?.locked ?? false;
  const playMode = playModeOf(event);
  const playerLabel = playMode.playerName;
  const [queuedSongs, setQueuedSongs] = useState<SongRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // Number of songs to show in prep-ahead view
  const PREP_AHEAD_COUNT = 5;

  // Load queued songs
  const loadQueuedSongs = async () => {
    if (!event?.id) return;

    const requests = await getRequestsByEventId(event.id);
    // Get only queued songs, sorted by manual order or vote count
    const queued = requests
      .filter(r => r.status === 'queued')
      .sort((a, b) => {
        const byPosition = compareManualPositions(a, b);
        if (byPosition !== 0) return byPosition;
        // Otherwise sort by vote count (highest first)
        return b.voteCount - a.voteCount;
      });

    setQueuedSongs(queued);
    setLoading(false);
  };

  useEffect(() => {
    loadQueuedSongs();
    // Refresh every 10 seconds
    const interval = setInterval(loadQueuedSongs, 10000);
    return () => clearInterval(interval);
  }, [event?.id]);

  // Toggle prep status for a song
  const togglePrepStatus = async (request: SongRequest) => {
    try {
      const newStatus = !request.preppedInSpotify;
      await updateRequest(request.id, { preppedInSpotify: newStatus });

      // Update local state
      setQueuedSongs(prev =>
        prev.map(r =>
          r.id === request.id ? { ...r, preppedInSpotify: newStatus } : r
        )
      );

      toast.success(
        newStatus
          ? `Marked "${request.song.title}" as prepped`
          : `Marked "${request.song.title}" as not prepped`
      );
    } catch (error) {
      toast.error('Failed to update prep status');
    }
  };

  // Get prep status display
  const getPrepStatusDisplay = (prepped: boolean) => {
    if (prepped) {
      return {
        icon: Check,
        text: 'Ready in Spotify',
        className: 'text-success bg-success/10 border-success/20',
        iconClass: 'text-success',
      };
    }
    return {
      icon: AlertTriangle,
      text: 'Not prepped',
      className: 'text-warning bg-warning/10 border-warning/20',
      iconClass: 'text-warning',
    };
  };

  // Get the next songs to prep
  const nextSongs = queuedSongs.slice(0, PREP_AHEAD_COUNT);
  const preppedCount = nextSongs.filter(s => s.preppedInSpotify).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Prep Ahead
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Prepare the next songs in your Spotify queue
          </p>
        </div>
        <button
          onClick={loadQueuedSongs}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Refresh queue"
        >
          <RefreshCw className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Prep Status Summary */}
      {nextSongs.length > 0 && (
        <div
          className={`p-4 rounded-lg border ${
            preppedCount === nextSongs.length
              ? 'bg-success/5 border-success/20'
              : preppedCount >= Math.ceil(nextSongs.length / 2)
              ? 'bg-warning/5 border-warning/20'
              : 'bg-danger/5 border-danger/20'
          }`}
        >
          <div className="flex items-center gap-3">
            {preppedCount === nextSongs.length ? (
              <Check className="w-5 h-5 text-success" />
            ) : (
              <Clock className="w-5 h-5 text-warning" />
            )}
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {preppedCount === nextSongs.length
                  ? 'All songs prepped!'
                  : `${preppedCount} of ${nextSongs.length} songs prepped`}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {preppedCount === nextSongs.length
                  ? 'You\'re ready to go!'
                  : 'Mark songs as prepped once you\'ve added them to your Spotify queue'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Next Songs List */}
      {nextSongs.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Layers className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Songs in Queue
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            Approve pending requests to add songs to your queue, then come back here to prep them for your event.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            Next {nextSongs.length} Song{nextSongs.length !== 1 ? 's' : ''}
          </h3>
          {nextSongs.map((song, index) => {
            const status = getPrepStatusDisplay(song.preppedInSpotify);
            const StatusIcon = status.icon;

            return (
              <div
                key={song.id}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg group"
              >
                {/* Position indicator */}
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-sm font-medium text-gray-600 dark:text-gray-400">
                  {index + 1}
                </div>

                {/* Album art */}
                {song.song.albumArtUrl ? (
                  <img
                    src={song.song.albumArtUrl}
                    alt={`${song.song.title} album art`}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Music className="w-6 h-6 text-gray-400" />
                  </div>
                )}

                {/* Song info */}
                <div className="flex-1 min-w-0">
                  <p
                    className="font-medium text-gray-900 dark:text-white truncate"
                    title={song.song.title}
                  >
                    {song.song.title}
                  </p>
                  <p
                    className="text-sm text-gray-600 dark:text-gray-400 truncate"
                    title={song.song.artist}
                  >
                    {song.song.artist}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {formatDuration(song.song.durationMs)} • {song.voteCount} vote{song.voteCount !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Prep status badge */}
                {!isLocked && (
                  <button
                    onClick={() => togglePrepStatus(song)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors hover:opacity-80 ${status.className}`}
                    aria-label={song.preppedInSpotify ? 'Mark as not prepped' : 'Mark as prepped'}
                  >
                    <StatusIcon className={`w-4 h-4 ${status.iconClass}`} />
                    <span>{status.text}</span>
                  </button>
                )}

                {/* Search/open in player */}
                {(() => {
                  const { url, isDirect } = openUrlFor(song, playMode);
                  return (
                    <button
                      onClick={() => {
                        if (url.startsWith('http://') || url.startsWith('https://')) {
                          window.open(url, '_blank', 'noopener');
                        } else {
                          window.location.href = url;
                        }
                      }}
                      className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      aria-label={isDirect ? `Open "${song.song.title}" in ${playerLabel}` : `Search "${song.song.title}" in ${playerLabel}`}
                      title={isDirect ? `Open in ${playerLabel}` : `Search in ${playerLabel}`}
                    >
                      <ExternalLink className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Remaining songs notice */}
      {queuedSongs.length > PREP_AHEAD_COUNT && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <ChevronRight className="w-4 h-4" />
          <span>
            {queuedSongs.length - PREP_AHEAD_COUNT} more song{queuedSongs.length - PREP_AHEAD_COUNT !== 1 ? 's' : ''} in queue
          </span>
        </div>
      )}

      {/* Tips */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h4 className="font-medium text-primary mb-2">How to Use Prep-Ahead</h4>
        <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
          <li>Click the Spotify icon to search for each song</li>
          <li>Add the song to your Spotify queue</li>
          <li>Click the status badge to mark it as "Ready in Spotify"</li>
          <li>When you're DJing, you'll know exactly what's coming up!</li>
        </ol>
      </div>
    </div>
  );
}
